'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { TradeDirectionMarker } from '@/components/market/identity/trade-direction-marker';
import { UserIdentity } from '@/components/profile/user-identity';
import { ThesisActions } from '@/components/social/debate/thesis-actions';
import { ThesisMarketAvatar } from '@/components/social/debate/thesis-market-avatar';
import { ThesisReplyThread } from '@/components/social/debate/thesis-reply-thread';
import { READ_ONLY_THESIS_REPLY_WRITE_ACCESS, type ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import { SolscanLink } from '@/components/social/shared/solscan-link';
import type { ThesisItem } from '@/components/social/types';
import { resolveProposalCategory } from '@/config/proposal-display';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useMarketDetailPrefetchIntent, useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FALLBACK_CATEGORY_EMOJI = '\u{1F4A1}';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Variant = 'debate' | 'portfolio';
interface ThesisCardProps {
  thesis: ThesisItem;
  isAuthenticated: boolean;
  userId: string | null;
  /** Shared market-level access controls writing only; threads stay public. */
  replyWriteAccess?: ThesisReplyWriteAccess;
  /** Opens the owning market's mobile trade sheet. */
  onOpenTradeForm?: () => void;
  /**
   * Discriminator between the two surfaces (spec 094):
   *
   * - `'debate'` (default) — rendered inside the market-detail Debate tab.
   *   Profile-picture avatar that routes to `/profile/{userId}`. The card body
   *   is not pressable; the viewer is already on the parent market. Replies
   *   expand inline.
   * - `'portfolio'` — rendered inside the portfolio Theses panel. Rounded
   *   card surface with an absolute-positioned `<Link>` overlay to the
   *   market's Debate tab anchored to this thesis. Avatar is a rounded-square
   *   market image with an outcome-colored status dot. Parent market title
   *   above each card uses the same typography as portfolio PositionRow market titles.
   *   Replies do not expand inline (the viewer is one tap away from the destination's full thread).
   */
  variant?: Variant;
  /** Cross-market feeds need a visible destination even with the debate presentation. */
  showMarketLink?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThesisCard(t0) {
  const $ = _c(128);
  const {
    thesis,
    isAuthenticated,
    userId,
    replyWriteAccess: t1,
    onOpenTradeForm,
    variant: t2,
    showMarketLink: t3
  } = t0;
  const replyWriteAccess = t1 === undefined ? READ_ONLY_THESIS_REPLY_WRITE_ACCESS : t1;
  const variant = t2 === undefined ? "debate" : t2;
  const showMarketLink = t3 === undefined ? false : t3;
  const locale = useLocale();
  const [showReplies, setShowReplies] = useState(false);
  const [replyCount, setReplyCount] = useState(thesis.replyCount);
  const tTimeIntl = useTranslations("time");
  const tDebate = useTranslations("social.debate");
  const {
    formatD6
  } = useFormattedCurrency();
  let t4;
  if ($[0] !== tTimeIntl) {
    t4 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const tTime = t4;
  const t5 = thesis.outcomeLabel ?? "";
  let t6;
  if ($[2] !== t5 || $[3] !== thesis.outcomeIndex) {
    t6 = {
      title: t5,
      index: thesis.outcomeIndex
    };
    $[2] = t5;
    $[3] = thesis.outcomeIndex;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== thesis.marketPubkey) {
    t7 = {
      marketPubkey: thesis.marketPubkey
    };
    $[5] = thesis.marketPubkey;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const {
    hue
  } = useOutcomeColor(t6, t7);
  const isBuy = thesis.tradeDirection === "buy";
  let t8;
  if ($[7] !== isBuy || $[8] !== tDebate) {
    t8 = isBuy ? tDebate("bought") : tDebate("sold");
    $[7] = isBuy;
    $[8] = tDebate;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const verb = t8;
  const outcomeLabel = thesis.outcomeLabel ?? "";
  let t9;
  if ($[10] !== formatD6 || $[11] !== thesis.tradeAmount) {
    t9 = formatD6(thesis.tradeAmount);
    $[10] = formatD6;
    $[11] = thesis.tradeAmount;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const tradeLineText = `${verb} ${outcomeLabel} for ${t9}`;
  const traderHref = `/portfolio/${thesis.trader.userId ?? ""}`;
  const marketHref = `/markets/${thesis.marketPubkey}`;
  const deepLinkHref = `/markets/${thesis.marketPubkey}?tab=debate&thesis=${thesis.signature}`;
  const traderIntent = useUserProfilePrefetchIntent(thesis.trader.userId);
  const marketIntent = useMarketDetailPrefetchIntent(thesis.marketPubkey);
  const deepLinkIntent = useIntentRoutePrefetch(deepLinkHref, marketIntent.onMouseEnter);
  const categoryEmoji = thesis.marketCategory.length > 0 ? resolveProposalCategory(thesis.marketCategory)?.emoji ?? FALLBACK_CATEGORY_EMOJI : FALLBACK_CATEGORY_EMOJI;
  let t10;
  if ($[13] !== replyWriteAccess || $[14] !== showReplies) {
    t10 = function handleToggleReplies() {
      if (!showReplies && replyWriteAccess.status === "ineligible") {
        replyWriteAccess.refresh();
      }
      setShowReplies(_temp);
    };
    $[13] = replyWriteAccess;
    $[14] = showReplies;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const handleToggleReplies = t10;
  let t11;
  if ($[16] !== thesis.trader) {
    t11 = <UserIdentity user={thesis.trader} size="md" nameClassName="text-foreground-secondary" handleClassName="text-body text-foreground-tertiary" />;
    $[16] = thesis.trader;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== traderHref || $[20] !== traderIntent) {
    t12 = <Link href={traderHref} prefetch={false} className="relative z-10 min-w-0 truncate hover:underline" {...traderIntent} onClick={_temp2}>{t11}</Link>;
    $[18] = t11;
    $[19] = traderHref;
    $[20] = traderIntent;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== locale || $[24] !== tTime || $[25] !== thesis.createdAt) {
    t14 = formatRelativeTime(thesis.createdAt, tTime, {
      locale
    });
    $[23] = locale;
    $[24] = tTime;
    $[25] = thesis.createdAt;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14) {
    t15 = <span className="shrink-0 text-body text-foreground-tertiary">{t14}</span>;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== thesis.signature) {
    t17 = <SolscanLink signature={thesis.signature} variant="inline" className="relative z-10" />;
    $[30] = thesis.signature;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t12 || $[33] !== t15 || $[34] !== t17) {
    t18 = <div className="flex flex-wrap items-center gap-1">{t12}{t13}{t15}{t16}{t17}</div>;
    $[32] = t12;
    $[33] = t15;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const metadataRow = t18;
  const t19 = `text-outcome-${hue}-text`;
  let t20;
  if ($[36] !== t19) {
    t20 = cn("text-body tabular-nums", t19);
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t20 || $[39] !== tradeLineText) {
    t21 = <p className={t20}>{tradeLineText}</p>;
    $[38] = t20;
    $[39] = tradeLineText;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const tradeLine = t21;
  let t22;
  if ($[41] !== thesis.content) {
    t22 = <p className="text-body leading-snug">{thesis.content}</p>;
    $[41] = thesis.content;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const body = t22;
  if (variant === "debate") {
    const t23 = `thesis-${thesis.signature}`;
    const t24 = thesis.trader.avatarNsfw ?? false;
    const t25 = thesis.trader.avatarPreferences?.rasterise ?? false;
    let t26;
    if ($[43] !== t24 || $[44] !== t25 || $[45] !== thesis.trader.avatarUrl || $[46] !== thesis.trader.displayName || $[47] !== thesis.trader.userId) {
      t26 = <UserAvatar userId={thesis.trader.userId} displayName={thesis.trader.displayName} avatarUrl={thesis.trader.avatarUrl} nsfw={t24} rasterise={t25} size="md" />;
      $[43] = t24;
      $[44] = t25;
      $[45] = thesis.trader.avatarUrl;
      $[46] = thesis.trader.displayName;
      $[47] = thesis.trader.userId;
      $[48] = t26;
    } else {
      t26 = $[48];
    }
    let t27;
    if ($[49] !== thesis.tradeDirection) {
      t27 = <TradeDirectionMarker direction={thesis.tradeDirection} />;
      $[49] = thesis.tradeDirection;
      $[50] = t27;
    } else {
      t27 = $[50];
    }
    let t28;
    if ($[51] !== t26 || $[52] !== t27) {
      t28 = <span className="relative block size-10">{t26}{t27}</span>;
      $[51] = t26;
      $[52] = t27;
      $[53] = t28;
    } else {
      t28 = $[53];
    }
    let t29;
    if ($[54] !== t28 || $[55] !== traderHref || $[56] !== traderIntent) {
      t29 = <Link href={traderHref} prefetch={false} className="shrink-0" {...traderIntent}>{t28}</Link>;
      $[54] = t28;
      $[55] = traderHref;
      $[56] = traderIntent;
      $[57] = t29;
    } else {
      t29 = $[57];
    }
    let t30;
    if ($[58] !== deepLinkHref || $[59] !== deepLinkIntent || $[60] !== showMarketLink || $[61] !== thesis.marketTitle) {
      t30 = showMarketLink ? <Link href={deepLinkHref} prefetch={false} className="truncate text-body font-medium text-foreground-primary hover:underline" title={thesis.marketTitle} {...deepLinkIntent}>{thesis.marketTitle}</Link> : null;
      $[58] = deepLinkHref;
      $[59] = deepLinkIntent;
      $[60] = showMarketLink;
      $[61] = thesis.marketTitle;
      $[62] = t30;
    } else {
      t30 = $[62];
    }
    let t31;
    if ($[63] !== body || $[64] !== metadataRow || $[65] !== t30 || $[66] !== tradeLine) {
      t31 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t30}{metadataRow}{tradeLine}{body}</div>;
      $[63] = body;
      $[64] = metadataRow;
      $[65] = t30;
      $[66] = tradeLine;
      $[67] = t31;
    } else {
      t31 = $[67];
    }
    let t32;
    if ($[68] !== t29 || $[69] !== t31) {
      t32 = <div className="flex gap-3">{t29}{t31}</div>;
      $[68] = t29;
      $[69] = t31;
      $[70] = t32;
    } else {
      t32 = $[70];
    }
    let t33;
    if ($[71] !== handleToggleReplies || $[72] !== isAuthenticated || $[73] !== replyCount || $[74] !== thesis) {
      t33 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} onToggleReplies={handleToggleReplies} /></div>;
      $[71] = handleToggleReplies;
      $[72] = isAuthenticated;
      $[73] = replyCount;
      $[74] = thesis;
      $[75] = t33;
    } else {
      t33 = $[75];
    }
    let t34;
    if ($[76] !== isAuthenticated || $[77] !== onOpenTradeForm || $[78] !== replyWriteAccess || $[79] !== showReplies || $[80] !== thesis.marketPubkey || $[81] !== thesis.signature || $[82] !== userId) {
      t34 = showReplies ? <div className="contents md:block md:pl-[52px]"><ThesisReplyThread thesisSignature={thesis.signature} marketPubkey={thesis.marketPubkey} isAuthenticated={isAuthenticated} userId={userId} writeAccess={replyWriteAccess} onClose={() => setShowReplies(false)} {...onOpenTradeForm !== undefined ? {
          onOpenTradeForm
        } : {}} onReplyAdded={() => setReplyCount(_temp3)} onReplyRemoved={() => setReplyCount(_temp4)} /></div> : null;
      $[76] = isAuthenticated;
      $[77] = onOpenTradeForm;
      $[78] = replyWriteAccess;
      $[79] = showReplies;
      $[80] = thesis.marketPubkey;
      $[81] = thesis.signature;
      $[82] = userId;
      $[83] = t34;
    } else {
      t34 = $[83];
    }
    let t35;
    if ($[84] !== t23 || $[85] !== t32 || $[86] !== t33 || $[87] !== t34) {
      t35 = <div id={t23} className="flex flex-col gap-2 px-0 pt-3 pb-2">{t32}{t33}{t34}</div>;
      $[84] = t23;
      $[85] = t32;
      $[86] = t33;
      $[87] = t34;
      $[88] = t35;
    } else {
      t35 = $[88];
    }
    return t35;
  }
  const t23 = `thesis-${thesis.signature}`;
  let t24;
  if ($[89] !== deepLinkHref || $[90] !== deepLinkIntent || $[91] !== thesis.marketTitle) {
    t24 = <Link href={deepLinkHref} prefetch={false} aria-label={thesis.marketTitle} className="absolute inset-0 z-0 rounded-panel-row focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary" {...deepLinkIntent} />;
    $[89] = deepLinkHref;
    $[90] = deepLinkIntent;
    $[91] = thesis.marketTitle;
    $[92] = t24;
  } else {
    t24 = $[92];
  }
  let t25;
  if ($[93] !== thesis.marketTitle) {
    t25 = <p className="truncate text-body font-medium text-foreground-primary" title={thesis.marketTitle}>{thesis.marketTitle}</p>;
    $[93] = thesis.marketTitle;
    $[94] = t25;
  } else {
    t25 = $[94];
  }
  let t26;
  if ($[95] !== categoryEmoji || $[96] !== hue || $[97] !== outcomeLabel || $[98] !== thesis.marketPubkey || $[99] !== thesis.outcomeImageNsfw || $[100] !== thesis.outcomeImageUrl || $[101] !== thesis.outcomeIndex) {
    t26 = <ThesisMarketAvatar marketPubkey={thesis.marketPubkey} outcomeIndex={thesis.outcomeIndex} outcomeTitle={outcomeLabel} outcomeImageUrl={thesis.outcomeImageUrl} outcomeImageNsfw={thesis.outcomeImageNsfw} categoryEmoji={categoryEmoji} hue={hue} size="md" />;
    $[95] = categoryEmoji;
    $[96] = hue;
    $[97] = outcomeLabel;
    $[98] = thesis.marketPubkey;
    $[99] = thesis.outcomeImageNsfw;
    $[100] = thesis.outcomeImageUrl;
    $[101] = thesis.outcomeIndex;
    $[102] = t26;
  } else {
    t26 = $[102];
  }
  let t27;
  if ($[103] !== marketHref || $[104] !== marketIntent || $[105] !== t26) {
    t27 = <Link href={marketHref} prefetch={false} className="relative z-10 shrink-0" {...marketIntent} onClick={_temp5}>{t26}</Link>;
    $[103] = marketHref;
    $[104] = marketIntent;
    $[105] = t26;
    $[106] = t27;
  } else {
    t27 = $[106];
  }
  let t28;
  if ($[107] !== body || $[108] !== metadataRow || $[109] !== tradeLine) {
    t28 = <div className="flex min-w-0 flex-1 flex-col gap-1">{tradeLine}{metadataRow}{body}</div>;
    $[107] = body;
    $[108] = metadataRow;
    $[109] = tradeLine;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== t27 || $[112] !== t28) {
    t29 = <div className="flex gap-3">{t27}{t28}</div>;
    $[111] = t27;
    $[112] = t28;
    $[113] = t29;
  } else {
    t29 = $[113];
  }
  let t30;
  if ($[114] !== isAuthenticated || $[115] !== replyCount || $[116] !== thesis) {
    t30 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} mode="display" /></div>;
    $[114] = isAuthenticated;
    $[115] = replyCount;
    $[116] = thesis;
    $[117] = t30;
  } else {
    t30 = $[117];
  }
  let t31;
  if ($[118] !== t29 || $[119] !== t30) {
    t31 = <div className="flex flex-col gap-2">{t29}{t30}</div>;
    $[118] = t29;
    $[119] = t30;
    $[120] = t31;
  } else {
    t31 = $[120];
  }
  let t32;
  if ($[121] !== t25 || $[122] !== t31) {
    t32 = <div className="flex flex-col gap-3">{t25}{t31}</div>;
    $[121] = t25;
    $[122] = t31;
    $[123] = t32;
  } else {
    t32 = $[123];
  }
  let t33;
  if ($[124] !== t23 || $[125] !== t24 || $[126] !== t32) {
    t33 = <div id={t23} className="relative rounded-panel-row border border-border-secondary p-3 transition-colors hover:bg-background-tertiary/80">{t24}{t32}</div>;
    $[124] = t23;
    $[125] = t24;
    $[126] = t32;
    $[127] = t33;
  } else {
    t33 = $[127];
  }
  return t33;
}
function _temp5(e_0) {
  e_0.stopPropagation();
}
function _temp4(c_0) {
  return Math.max(0, c_0 - 1);
}
function _temp3(c) {
  return c + 1;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(visible) {
  return !visible;
}